import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Upload, Loader2, ArrowRight, CreditCard, Check, X } from "lucide-react";
import { toast } from "sonner";
import { createOrder, createCheckoutSession } from "../lib/api";
import {
  trackBeginCheckout,
  trackPhotoUpload,
  trackAddShippingInfo,
  trackInitiatePayment,
} from "../lib/analytics";

const MAX_FILE_SIZE = 8 * 1024 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function OrderDialog({ style, open, onClose }) {
  const [step, setStep] = React.useState(1);
  const [loading, setLoading] = React.useState(false);

  const [photoDataUrl, setPhotoDataUrl] = React.useState("");
  const [photoName, setPhotoName] = React.useState("");
  const [includePetName, setIncludePetName] = React.useState(false);
  const [petName, setPetName] = React.useState("");

  const [form, setForm] = React.useState({
    customer_name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });

  React.useEffect(() => {
    if (!open) {
      setStep(1);
      setLoading(false);
      setPhotoDataUrl("");
      setPhotoName("");
      setIncludePetName(false);
      setPetName("");
      setForm({ customer_name: "", email: "", phone: "", address: "", notes: "" });
    }
  }, [open]);

  React.useEffect(() => {
    if (open && style) trackBeginCheckout(style.id, style.name);
  }, [open, style]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Archivo inválido", { description: "Sube una imagen (jpg o png)." });
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      toast.error("Archivo demasiado grande", { description: "El tamaño máximo es 8 MB." });
      return;
    }
    try {
      const dataUrl = await fileToBase64(f);
      setPhotoDataUrl(dataUrl);
      setPhotoName(f.name);
      trackPhotoUpload(style?.id);
    } catch (err) {
      toast.error("No se pudo leer la foto");
    }
  };

  const canStep1 = !!photoDataUrl && (!includePetName || petName.trim().length > 0);
  const canStep2 =
    form.customer_name.trim() && form.email.trim() && form.address.trim();

  const goPay = async () => {
    if (!style) return;
    setLoading(true);
    try {
      const origin = window.location.origin;
      const orderRes = await createOrder({
        style_id: style.id,
        customer_name: form.customer_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        pet_name: includePetName ? petName.trim() : "",
        include_pet_name: includePetName,
        notes: form.notes.trim(),
        photo_base64: photoDataUrl,
        origin_url: origin,
      });

      const checkout = await createCheckoutSession(orderRes.order_id, origin);
      if (checkout?.url) {
        trackInitiatePayment(style.id);
        window.location.href = checkout.url;
      } else {
        throw new Error("No se recibió la URL de pago");
      }
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || err.message || "Error inesperado";
      toast.error("No pudimos iniciar el pago", { description: String(detail) });
      setLoading(false);
    }
  };

  if (!style) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[620px] bg-[#f5f1e8] border-[#e8ddc3] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#b59557]">
              Pedido · {style.size || "20×15×3 cm"}
            </div>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  className={`h-1.5 w-8 rounded-full ${
                    step >= n ? "bg-[#b59557]" : "bg-[#e8ddc3]"
                  }`}
                />
              ))}
            </div>
          </div>
          <DialogTitle className="font-serif text-2xl md:text-3xl text-[#1a1816]">
            Retrato <span className="italic text-[#b59557] font-light">{style.name}</span>
          </DialogTitle>
          <DialogDescription className="text-[#4a4136]">
            {step === 1 && "Sube la foto de tu mascota y elige si quieres su nombre."}
            {step === 2 && "Tus datos de contacto y envío."}
            {step === 3 && "Revisa tu pedido y procede al pago seguro."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-5">
            <div>
              <Label className="mb-2 block">Foto de tu mascota</Label>
              {photoDataUrl ? (
                <div className="relative rounded-xl overflow-hidden border border-[#d6cdb9] bg-white">
                  <img src={photoDataUrl} alt="preview" className="w-full h-56 object-cover" />
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoDataUrl("");
                      setPhotoName("");
                    }}
                    className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                    aria-label="Quitar foto"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 px-3 py-2 text-xs text-white bg-black/50 truncate">
                    {photoName}
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[#d6cdb9] bg-white/60 px-4 py-10 cursor-pointer hover:border-[#b59557] transition-colors">
                  <Upload className="h-6 w-6 text-[#b59557]" />
                  <span className="text-sm text-[#4a4136]">
                    Haz clic para subir la foto (jpg, png · máx 8 MB)
                  </span>
                  <span className="text-xs text-[#8a7340]">Resolución mínima recomendada: 1500×1000 px</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="order-photo-input" />
                </label>
              )}
            </div>

            <div className="rounded-xl border border-[#e8ddc3] bg-white/70 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium text-[#1a1816]">Incluir el nombre de mi mascota</div>
                  <p className="text-xs text-[#4a4136] mt-1">
                    Lo añadiremos sobre la madera blanca con una tipografía elegante.
                  </p>
                </div>
                <Switch checked={includePetName} onCheckedChange={setIncludePetName} />
              </div>
              {includePetName && (
                <div className="mt-4">
                  <Label htmlFor="petName">Nombre de la mascota</Label>
                  <Input
                    id="petName"
                    value={petName}
                    onChange={(e) => setPetName(e.target.value)}
                    placeholder="Ej: Kiro"
                    maxLength={24}
                    className="mt-1.5"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                <div className="font-serif italic text-2xl text-[#8a7340]">{style.price || "29,50€"}</div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#7a6a48]">Envío incluido</div>
              </div>
              <button
                disabled={!canStep1}
                onClick={() => setStep(2)}
                data-testid="order-step1-next"
                className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium ${
                  canStep1 ? "btn-primary" : "bg-[#d6cdb9] text-white/90 cursor-not-allowed"
                }`}
              >
                Continuar <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="cn">Nombre completo</Label>
                <Input id="cn" data-testid="order-name-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Tu nombre" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="em">Email</Label>
                <Input id="em" data-testid="order-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="tu@email.com" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="ph">Teléfono (opcional)</Label>
                <Input id="ph" data-testid="order-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+34 600 000 000" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ad">Dirección de envío</Label>
                <Input id="ad" data-testid="order-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Calle, nº, CP, ciudad" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nt">Notas (opcional)</Label>
              <Textarea id="nt" data-testid="order-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Algún detalle que quieras que tengamos en cuenta" rows={3} />
            </div>

            <div className="flex items-center justify-between pt-2">
              <button onClick={() => setStep(1)} className="btn-ghost rounded-full px-5 py-2.5 text-sm">
                Atrás
              </button>
              <button
                disabled={!canStep2}
                onClick={() => {
                  trackAddShippingInfo(style.id);
                  setStep(3);
                }}
                data-testid="order-step2-next"
                className={`inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium ${
                  canStep2 ? "btn-primary" : "bg-[#d6cdb9] text-white/90 cursor-not-allowed"
                }`}
              >
                Revisar pedido <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div className="rounded-xl border border-[#e8ddc3] bg-white overflow-hidden">
              <div className="flex items-stretch">
                {photoDataUrl && (
                  <img src={photoDataUrl} alt="foto" className="w-28 h-28 sm:w-32 sm:h-32 object-cover" />
                )}
                <div className="p-4 flex-1">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-[#b59557]">Estilo</div>
                  <div className="font-serif text-xl text-[#1a1816]">{style.name}</div>
                  {includePetName && petName && (
                    <div className="mt-1 text-sm text-[#4a4136]">
                      Con el nombre: <span className="italic text-[#8a7340]">{petName}</span>
                    </div>
                  )}
                  <div className="mt-1 text-xs text-[#7a6a48]">{style.size || "20×15×3 cm"} · Envío incluido</div>
                </div>
                <div className="p-4 text-right">
                  <div className="font-serif italic text-2xl text-[#8a7340]">{style.price || "29,50€"}</div>
                </div>
              </div>
              <div className="px-4 py-3 border-t border-[#e8ddc3] text-sm text-[#4a4136] space-y-1">
                <div><span className="text-[#7a6a48]">Cliente:</span> {form.customer_name}</div>
                <div><span className="text-[#7a6a48]">Email:</span> {form.email}</div>
                {form.phone && (<div><span className="text-[#7a6a48]">Teléfono:</span> {form.phone}</div>)}
                <div><span className="text-[#7a6a48]">Envío a:</span> {form.address}</div>
                {form.notes && (<div><span className="text-[#7a6a48]">Notas:</span> {form.notes}</div>)}
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-[#4a4136] bg-[#efe6cf]/60 border border-[#e8ddc3] rounded-xl px-4 py-3">
              <Check className="h-4 w-4 text-[#8a7340]" />
              Pago seguro con tarjeta procesado por Stripe.
            </div>

            <div className="flex items-center justify-between pt-2">
              <button onClick={() => setStep(2)} disabled={loading} className="btn-ghost rounded-full px-5 py-2.5 text-sm">
                Atrás
              </button>
              <button onClick={goPay} disabled={loading} data-testid="order-pay-button" className="btn-primary rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2">
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Procesando...</>
                ) : (
                  <><CreditCard className="h-4 w-4" /> Pagar con tarjeta · {style.price || "29,50€"}</>
                )}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
